import { useEffect, useRef, useState, useCallback } from 'react';

// ── Types ──────────────────────────────────────────────────────────────
interface Port {
  id: string;
  label: string;
  x: number; // 0–100 viewport %
  y: number;
  major: boolean;
  region: 'asia' | 'europe' | 'americas' | 'africa' | 'oceania';
}

interface Lane {
  from: string;
  to: string;
  weight: number; // 1–3, affects line thickness & cargo frequency
}

interface CargoParticle {
  id: number;
  laneIdx: number;
  t: number;       // 0–1 progress along path
  speed: number;
  size: number;
  opacity: number;
  trail: { x: number; y: number }[];
}

interface FloatingDot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  pulse: number;
  pulseSpeed: number;
}

// ── Port data (real maritime positions mapped to viewport) ─────────────
const PORTS: Port[] = [
  { id: 'mumbai',    label: 'Mumbai',       x: 72, y: 52, major: true,  region: 'asia' },
  { id: 'chennai',  label: 'Chennai',       x: 75, y: 60, major: false, region: 'asia' },
  { id: 'delhi',    label: 'New Delhi',     x: 72, y: 42, major: true,  region: 'asia' },
  { id: 'shanghai', label: 'Shanghai',      x: 88, y: 38, major: true,  region: 'asia' },
  { id: 'singapore',label: 'Singapore',     x: 84, y: 62, major: true,  region: 'asia' },
  { id: 'dubai',    label: 'Dubai',         x: 66, y: 48, major: true,  region: 'asia' },
  { id: 'tokyo',    label: 'Tokyo',         x: 93, y: 35, major: false, region: 'asia' },
  { id: 'bangkok',  label: 'Bangkok',       x: 83, y: 56, major: false, region: 'asia' },
  { id: 'london',   label: 'London',        x: 46, y: 22, major: true,  region: 'europe' },
  { id: 'hamburg',  label: 'Hamburg',       x: 51, y: 18, major: false, region: 'europe' },
  { id: 'rotterdam',label: 'Rotterdam',     x: 49, y: 20, major: true,  region: 'europe' },
  { id: 'barcelona',label: 'Barcelona',     x: 46, y: 28, major: false, region: 'europe' },
  { id: 'istanbul', label: 'Istanbul',      x: 58, y: 28, major: false, region: 'europe' },
  { id: 'newyork',  label: 'New York',      x: 18, y: 28, major: true,  region: 'americas' },
  { id: 'losangeles',label: 'Los Angeles',  x: 7,  y: 36, major: true,  region: 'americas' },
  { id: 'houston',  label: 'Houston',       x: 14, y: 42, major: false, region: 'americas' },
  { id: 'miami',    label: 'Miami',         x: 18, y: 44, major: false, region: 'americas' },
  { id: 'toronto',  label: 'Toronto',       x: 17, y: 22, major: false, region: 'americas' },
  { id: 'saopaulo', label: 'São Paulo',     x: 24, y: 72, major: true,  region: 'americas' },
  { id: 'buenos',   label: 'Buenos Aires',  x: 22, y: 82, major: false, region: 'americas' },
  { id: 'lagos',    label: 'Lagos',         x: 44, y: 60, major: false, region: 'africa' },
  { id: 'capetown', label: 'Cape Town',     x: 50, y: 82, major: true,  region: 'africa' },
  { id: 'nairobi',  label: 'Nairobi',       x: 60, y: 64, major: false, region: 'africa' },
  { id: 'sydney',   label: 'Sydney',        x: 91, y: 78, major: true,  region: 'oceania' },
  { id: 'melbourne',label: 'Melbourne',     x: 89, y: 82, major: false, region: 'oceania' },
];

// ── Trade lanes ────────────────────────────────────────────────────────
const LANES: Lane[] = [
  // India hub — primary arteries
  { from: 'mumbai',    to: 'dubai',      weight: 3 },
  { from: 'mumbai',    to: 'rotterdam',  weight: 3 },
  { from: 'mumbai',    to: 'newyork',    weight: 3 },
  { from: 'mumbai',    to: 'singapore',  weight: 3 },
  { from: 'mumbai',    to: 'shanghai',   weight: 2 },
  { from: 'mumbai',    to: 'capetown',   weight: 2 },
  { from: 'mumbai',    to: 'sydney',     weight: 2 },
  { from: 'mumbai',    to: 'lagos',      weight: 2 },
  { from: 'mumbai',    to: 'london',     weight: 3 },
  { from: 'mumbai',    to: 'losangeles', weight: 2 },
  { from: 'mumbai',    to: 'saopaulo',   weight: 2 },
  { from: 'chennai',   to: 'singapore',  weight: 2 },
  { from: 'chennai',   to: 'dubai',      weight: 2 },
  { from: 'delhi',     to: 'dubai',      weight: 2 },
  { from: 'delhi',     to: 'london',     weight: 2 },
  // Asia cross
  { from: 'shanghai',  to: 'losangeles', weight: 3 },
  { from: 'shanghai',  to: 'rotterdam',  weight: 3 },
  { from: 'shanghai',  to: 'singapore',  weight: 2 },
  { from: 'singapore', to: 'sydney',     weight: 2 },
  { from: 'singapore', to: 'rotterdam',  weight: 2 },
  { from: 'tokyo',     to: 'losangeles', weight: 2 },
  { from: 'tokyo',     to: 'shanghai',   weight: 2 },
  { from: 'dubai',     to: 'rotterdam',  weight: 2 },
  { from: 'dubai',     to: 'istanbul',   weight: 2 },
  // Europe
  { from: 'rotterdam', to: 'newyork',    weight: 3 },
  { from: 'rotterdam', to: 'london',     weight: 2 },
  { from: 'london',    to: 'newyork',    weight: 3 },
  { from: 'hamburg',   to: 'newyork',    weight: 2 },
  { from: 'barcelona', to: 'newyork',    weight: 1 },
  { from: 'istanbul',  to: 'rotterdam',  weight: 1 },
  // Americas
  { from: 'newyork',   to: 'losangeles', weight: 2 },
  { from: 'newyork',   to: 'miami',      weight: 1 },
  { from: 'newyork',   to: 'saopaulo',   weight: 2 },
  { from: 'losangeles',to: 'sydney',     weight: 2 },
  { from: 'houston',   to: 'rotterdam',  weight: 1 },
  { from: 'saopaulo',  to: 'capetown',   weight: 2 },
  { from: 'saopaulo',  to: 'rotterdam',  weight: 2 },
  // Africa
  { from: 'capetown',  to: 'rotterdam',  weight: 2 },
  { from: 'capetown',  to: 'sydney',     weight: 1 },
  { from: 'lagos',     to: 'rotterdam',  weight: 1 },
  { from: 'nairobi',   to: 'dubai',      weight: 1 },
];

// ── Seeded random ──────────────────────────────────────────────────────
const sr = (seed: number) => {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
};

// ── Bezier helpers ─────────────────────────────────────────────────────
const getBezierPoint = (
  t: number,
  x0: number, y0: number,
  cx: number, cy: number,
  x1: number, y1: number
) => {
  const mt = 1 - t;
  return {
    x: mt * mt * x0 + 2 * mt * t * cx + t * t * x1,
    y: mt * mt * y0 + 2 * mt * t * cy + t * t * y1,
  };
};

const getControlPoint = (
  from: Port, to: Port, idx: number,
  mouseX: number, mouseY: number
) => {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const perpX = -dy / len;
  const perpY = dx / len;
  const curvature = (sr(idx * 7 + 3) - 0.5) * 18;
  const mouseDist = Math.sqrt((mouseX - midX) ** 2 + (mouseY - midY) ** 2);
  const influence = Math.max(0, 1 - mouseDist / 40) * 8;
  const mDx = (mouseX - midX) * 0.015 * influence;
  const mDy = (mouseY - midY) * 0.015 * influence;
  return {
    cx: midX + perpX * curvature + mDx,
    cy: midY + perpY * curvature + mDy,
  };
};

// ── Component ──────────────────────────────────────────────────────────
const HeroAnimation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: 50, y: 50, rawX: 0, rawY: 0 });
  const scrollRef = useRef(0);
  const hoveredPortRef = useRef<string | null>(null);
  const hoveredLaneRef = useRef<number | null>(null);
  const particlesRef = useRef<CargoParticle[]>([]);
  const floatingDotsRef = useRef<FloatingDot[]>([]);
  const rafRef = useRef<number>(0);
  const timeRef = useRef(0);
  const [hoveredPort, setHoveredPort] = useState<string | null>(null);

  const portMapRef = useRef<Map<string, Port>>(new Map(PORTS.map(p => [p.id, p])));

  // ── Init particles ───────────────────────────────────────────────────
  useEffect(() => {
    const particles: CargoParticle[] = [];
    let id = 0;
    LANES.forEach((lane, laneIdx) => {
      const count = lane.weight * 2 + 1;
      for (let i = 0; i < count; i++) {
        particles.push({
          id: id++,
          laneIdx,
          t: sr(id * 17 + laneIdx * 31),
          speed: (0.0008 + sr(id * 7) * 0.0012) * (0.6 + lane.weight * 0.2),
          size: 1.5 + sr(id * 13) * 2.5,
          opacity: 0.5 + sr(id * 19) * 0.5,
          trail: [],
        });
      }
    });
    particlesRef.current = particles;

    // Floating ambient dots
    const dots: FloatingDot[] = [];
    for (let i = 0; i < 60; i++) {
      dots.push({
        x: sr(i * 11) * 100,
        y: sr(i * 17) * 100,
        vx: (sr(i * 23) - 0.5) * 0.015,
        vy: (sr(i * 29) - 0.5) * 0.008,
        size: 0.5 + sr(i * 37) * 1.5,
        opacity: 0.1 + sr(i * 41) * 0.25,
        pulse: sr(i * 43) * Math.PI * 2,
        pulseSpeed: 0.01 + sr(i * 47) * 0.02,
      });
    }
    floatingDotsRef.current = dots;
  }, []);

  // ── Mouse tracking ───────────────────────────────────────────────────
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const px = ((e.clientX - rect.left) / rect.width) * 100;
      const py = ((e.clientY - rect.top) / rect.height) * 100;
      mouseRef.current = { x: px, y: py, rawX: e.clientX - rect.left, rawY: e.clientY - rect.top };

      // Check port hover
      let found: string | null = null;
      let foundLane: number | null = null;
      PORTS.forEach(port => {
        const dx = px - port.x;
        const dy = py - port.y;
        if (Math.sqrt(dx * dx + dy * dy) < 3) found = port.id;
      });
      if (!found) {
        // Check lane hover
        LANES.forEach((lane, idx) => {
          const from = portMapRef.current.get(lane.from);
          const to = portMapRef.current.get(lane.to);
          if (!from || !to) return;
          const { cx, cy } = getControlPoint(from, to, idx, px, py);
          for (let t = 0; t <= 1; t += 0.05) {
            const pt = getBezierPoint(t, from.x, from.y, cx, cy, to.x, to.y);
            if (Math.sqrt((px - pt.x) ** 2 + (py - pt.y) ** 2) < 1.5) {
              foundLane = idx;
            }
          }
        });
      }
      hoveredPortRef.current = found;
      hoveredLaneRef.current = foundLane;
      setHoveredPort(found);
    };

    const handleScroll = () => {
      scrollRef.current = window.scrollY;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // ── Main render loop ─────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const mouse = mouseRef.current;
    const scroll = scrollRef.current;
    const t = timeRef.current;
    timeRef.current += 1;

    // Scroll-based zoom/pan
    const scrollProgress = Math.min(scroll / 600, 1);
    const zoom = 1 + scrollProgress * 0.12;
    const panY = scrollProgress * -4; // % units

    ctx.clearRect(0, 0, W, H);

    // ── Background gradient ──────────────────────────────────────────
    const bg = ctx.createRadialGradient(W * 0.5, H * 0.45, 0, W * 0.5, H * 0.5, W * 0.8);
    bg.addColorStop(0, '#0d1f35');
    bg.addColorStop(0.4, '#091628');
    bg.addColorStop(1, '#050d18');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Helper: convert % coords to canvas px with zoom/pan/parallax
    const toCanvas = (px: number, py: number, depth = 1) => {
      const parallaxX = (mouse.x - 50) * 0.04 * depth;
      const parallaxY = (mouse.y - 50) * 0.025 * depth;
      const cx = ((px + parallaxX) / 100) * W;
      const cy = (((py + panY) + parallaxY) / 100) * H;
      // Apply zoom from center
      const ox = W / 2;
      const oy = H / 2;
      return {
        x: ox + (cx - ox) * zoom,
        y: oy + (cy - oy) * zoom,
      };
    };

    // ── Floating ambient particles ───────────────────────────────────
    floatingDotsRef.current.forEach(dot => {
      dot.x += dot.vx;
      dot.y += dot.vy;
      dot.pulse += dot.pulseSpeed;
      if (dot.x < 0) dot.x = 100;
      if (dot.x > 100) dot.x = 0;
      if (dot.y < 0) dot.y = 100;
      if (dot.y > 100) dot.y = 0;
      const pos = toCanvas(dot.x, dot.y, 0.3);
      const pulsedOpacity = dot.opacity * (0.6 + 0.4 * Math.sin(dot.pulse));
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, dot.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212, 175, 55, ${pulsedOpacity * 0.3})`;
      ctx.fill();
    });

    // ── Ocean depth lines (latitude-like) ───────────────────────────
    for (let i = 0; i < 8; i++) {
      const yPct = 10 + i * 11;
      const pos1 = toCanvas(0, yPct, 0.1);
      const pos2 = toCanvas(100, yPct, 0.1);
      ctx.beginPath();
      ctx.moveTo(pos1.x, pos1.y);
      ctx.lineTo(pos2.x, pos2.y);
      ctx.strokeStyle = `rgba(255,255,255,0.018)`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
    for (let i = 0; i < 12; i++) {
      const xPct = i * 9;
      const pos1 = toCanvas(xPct, 0, 0.1);
      const pos2 = toCanvas(xPct, 100, 0.1);
      ctx.beginPath();
      ctx.moveTo(pos1.x, pos1.y);
      ctx.lineTo(pos2.x, pos2.y);
      ctx.strokeStyle = `rgba(255,255,255,0.012)`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // ── Trade lanes ──────────────────────────────────────────────────
    LANES.forEach((lane, laneIdx) => {
      const from = portMapRef.current.get(lane.from);
      const to = portMapRef.current.get(lane.to);
      if (!from || !to) return;

      const { cx: cpx, cy: cpy } = getControlPoint(from, to, laneIdx, mouse.x, mouse.y);
      const fromPos = toCanvas(from.x, from.y, 0.8);
      const toPos = toCanvas(to.x, to.y, 0.8);
      const cpPos = toCanvas(cpx, cpy, 0.8);

      const isHoveredLane = hoveredLaneRef.current === laneIdx;
      const isConnectedToHoveredPort =
        hoveredPortRef.current === lane.from || hoveredPortRef.current === lane.to;
      const isActive = isHoveredLane || isConnectedToHoveredPort;

      // Glow pass
      if (isActive) {
        ctx.beginPath();
        ctx.moveTo(fromPos.x, fromPos.y);
        ctx.quadraticCurveTo(cpPos.x, cpPos.y, toPos.x, toPos.y);
        ctx.strokeStyle = `rgba(212, 175, 55, 0.25)`;
        ctx.lineWidth = 6;
        ctx.filter = 'blur(4px)';
        ctx.stroke();
        ctx.filter = 'none';
      }

      // Main lane line
      ctx.beginPath();
      ctx.moveTo(fromPos.x, fromPos.y);
      ctx.quadraticCurveTo(cpPos.x, cpPos.y, toPos.x, toPos.y);

      const baseOpacity = isActive ? 0.55 : 0.08 + lane.weight * 0.04;
      const gradient = ctx.createLinearGradient(fromPos.x, fromPos.y, toPos.x, toPos.y);

      const fromIsIndia = lane.from === 'mumbai' || lane.from === 'chennai' || lane.from === 'delhi';
      const toIsIndia = lane.to === 'mumbai' || lane.to === 'chennai' || lane.to === 'delhi';

      if (fromIsIndia || toIsIndia) {
        gradient.addColorStop(0, `rgba(212, 175, 55, ${baseOpacity * 1.4})`);
        gradient.addColorStop(0.5, `rgba(255, 210, 80, ${baseOpacity * 1.2})`);
        gradient.addColorStop(1, `rgba(212, 175, 55, ${baseOpacity * 1.4})`);
      } else {
        gradient.addColorStop(0, `rgba(180, 200, 230, ${baseOpacity})`);
        gradient.addColorStop(0.5, `rgba(200, 220, 255, ${baseOpacity * 0.8})`);
        gradient.addColorStop(1, `rgba(180, 200, 230, ${baseOpacity})`);
      }

      ctx.strokeStyle = gradient;
      ctx.lineWidth = isActive ? 1.5 : 0.5 + lane.weight * 0.3;
      ctx.setLineDash(isActive ? [] : [2, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    // ── Cargo particles ──────────────────────────────────────────────
    particlesRef.current.forEach(particle => {
      const lane = LANES[particle.laneIdx];
      const from = portMapRef.current.get(lane.from);
      const to = portMapRef.current.get(lane.to);
      if (!from || !to) return;

      particle.t += particle.speed;
      if (particle.t > 1) particle.t -= 1;

      const { cx: cpx, cy: cpy } = getControlPoint(from, to, particle.laneIdx, mouse.x, mouse.y);
      const pt = getBezierPoint(particle.t, from.x, from.y, cpx, cpy, to.x, to.y);
      const pos = toCanvas(pt.x, pt.y, 0.8);

      // Trail
      particle.trail.push({ x: pos.x, y: pos.y });
      if (particle.trail.length > 12) particle.trail.shift();

      const isActive =
        hoveredPortRef.current === lane.from ||
        hoveredPortRef.current === lane.to ||
        hoveredLaneRef.current === particle.laneIdx;

      const isIndia = lane.from === 'mumbai' || lane.from === 'chennai' || lane.from === 'delhi' ||
        lane.to === 'mumbai' || lane.to === 'chennai' || lane.to === 'delhi';

      const baseColor = isIndia ? '212, 175, 55' : '180, 210, 255';
      const opacityMult = isActive ? 1.8 : 1;

      // Draw trail
      if (particle.trail.length > 2) {
        for (let i = 1; i < particle.trail.length; i++) {
          const trailOpacity = (i / particle.trail.length) * particle.opacity * 0.4 * opacityMult;
          ctx.beginPath();
          ctx.moveTo(particle.trail[i - 1].x, particle.trail[i - 1].y);
          ctx.lineTo(particle.trail[i].x, particle.trail[i].y);
          ctx.strokeStyle = `rgba(${baseColor}, ${trailOpacity})`;
          ctx.lineWidth = particle.size * 0.4 * (i / particle.trail.length);
          ctx.stroke();
        }
      }

      // Glow halo
      const glow = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, particle.size * 3);
      glow.addColorStop(0, `rgba(${baseColor}, ${particle.opacity * 0.6 * opacityMult})`);
      glow.addColorStop(1, `rgba(${baseColor}, 0)`);
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, particle.size * 3, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // Core dot
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, particle.size * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${baseColor}, ${particle.opacity * opacityMult})`;
      ctx.fill();
    });

    // ── Port nodes ───────────────────────────────────────────────────
    PORTS.forEach(port => {
      const pos = toCanvas(port.x, port.y, 1);
      const isHovered = hoveredPortRef.current === port.id;
      const isIndia = port.region === 'asia' && (port.id === 'mumbai' || port.id === 'delhi' || port.id === 'chennai');
      const isMumbai = port.id === 'mumbai';

      const baseR = isMumbai ? 5 : port.major ? 3.5 : 2;
      const r = isHovered ? baseR * 1.6 : baseR;

      // Pulse ring for Mumbai
      if (isMumbai) {
        const pulseR = baseR + 6 + Math.sin(t * 0.04) * 4;
        const pulseOpacity = 0.15 + Math.sin(t * 0.04) * 0.1;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, pulseR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(212, 175, 55, ${pulseOpacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        const pulseR2 = baseR + 12 + Math.sin(t * 0.04 + 1) * 4;
        const pulseOpacity2 = 0.08 + Math.sin(t * 0.04 + 1) * 0.05;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, pulseR2, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(212, 175, 55, ${pulseOpacity2})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      // Hover ring
      if (isHovered) {
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r + 5, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(212, 175, 55, 0.3)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Outer ring
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r + 2, 0, Math.PI * 2);
      ctx.strokeStyle = isIndia
        ? `rgba(212, 175, 55, ${isHovered ? 0.7 : 0.3})`
        : `rgba(180, 210, 255, ${isHovered ? 0.5 : 0.15})`;
      ctx.lineWidth = 0.8;
      ctx.stroke();

      // Glow
      const nodeGlow = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, r * 4);
      nodeGlow.addColorStop(0, isIndia
        ? `rgba(212, 175, 55, ${isHovered ? 0.5 : 0.2})`
        : `rgba(180, 210, 255, ${isHovered ? 0.3 : 0.1})`);
      nodeGlow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r * 4, 0, Math.PI * 2);
      ctx.fillStyle = nodeGlow;
      ctx.fill();

      // Core
      const coreGrad = ctx.createRadialGradient(pos.x - r * 0.3, pos.y - r * 0.3, 0, pos.x, pos.y, r);
      if (isIndia) {
        coreGrad.addColorStop(0, '#fff8dc');
        coreGrad.addColorStop(0.5, '#D4AF37');
        coreGrad.addColorStop(1, '#a07820');
      } else {
        coreGrad.addColorStop(0, '#e8f4ff');
        coreGrad.addColorStop(0.5, '#90b8e0');
        coreGrad.addColorStop(1, '#4a7aaa');
      }
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();

      // Label
      if (port.major || isHovered) {
        const labelOpacity = isHovered ? 1 : isMumbai ? 0.9 : 0.55;
        ctx.font = `${isMumbai ? 'bold ' : ''}${isMumbai ? 11 : 9}px 'Inter', sans-serif`;
        ctx.fillStyle = `rgba(255, 255, 255, ${labelOpacity})`;
        ctx.textAlign = 'center';
        ctx.fillText(port.label, pos.x, pos.y - r - 5);
      }
    });

    // ── Center vignette for text readability ─────────────────────────
    const vignette = ctx.createRadialGradient(W * 0.5, H * 0.46, 0, W * 0.5, H * 0.5, W * 0.45);
    vignette.addColorStop(0, 'rgba(9, 22, 40, 0.88)');
    vignette.addColorStop(0.35, 'rgba(9, 22, 40, 0.55)');
    vignette.addColorStop(0.6, 'rgba(9, 22, 40, 0.15)');
    vignette.addColorStop(1, 'rgba(9, 22, 40, 0)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, W, H);

    rafRef.current = requestAnimationFrame(draw);
  }, []);

  // ── Canvas resize ────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resize = () => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // ── Start loop ───────────────────────────────────────────────────────
  useEffect(() => {
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Hover tooltip */}
      {hoveredPort && (() => {
        const port = PORTS.find(p => p.id === hoveredPort);
        if (!port) return null;
        const connectedLanes = LANES.filter(l => l.from === hoveredPort || l.to === hoveredPort);
        const partners = connectedLanes.map(l => {
          const otherId = l.from === hoveredPort ? l.to : l.from;
          return portMapRef.current.get(otherId)?.label;
        }).filter(Boolean).slice(0, 4);

        return (
          <div
            className="absolute pointer-events-none z-20 bg-navy-primary/90 backdrop-blur-sm border border-gold-accent/30 rounded-lg px-3 py-2 text-xs"
            style={{
              left: `${port.x}%`,
              top: `${port.y}%`,
              transform: 'translate(12px, -50%)',
            }}
          >
            <div className="text-gold-accent font-semibold text-sm mb-1">{port.label}</div>
            <div className="text-white/50 text-[10px] mb-1">Trade connections:</div>
            {partners.map(p => (
              <div key={p} className="text-white/70 text-[10px] flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-gold-accent/60 inline-block"></span>
                {p}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Scroll hint shimmer at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, rgba(5,13,24,0.6) 0%, transparent 100%)'
        }}
      />
    </div>
  );
};

export default HeroAnimation;
